import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * Camera tier picker quantity sync (1–4 per add-to-cart).
 * Loaded only with the how-many-cameras block when the product metafield is true.
 * Does not replace quantity-selector-component on other products; AI Guard ATC is unchanged.
 *
 * @extends {QuantitySelectorComponent}
 */
class QuboCameraQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * @returns {number | null}
   */
  #getVariantQuantityMax() {
    return parseIntOrDefault(this.dataset.variantQtyMax, null);
  }

  /**
   * Per-add tier cap (1–4), not reduced by items already in cart.
   * @returns {number | null}
   */
  getEffectiveMax() {
    const { max, min } = this.getCurrentValues();
    if (max === null) return null;
    return Math.max(max, min);
  }

  /**
   * @returns {number | null}
   */
  #getInventoryQuantityMax() {
    const root = this.closest('[data-qubo-camera-quantity]');
    if (!(root instanceof HTMLElement)) return null;

    const managed = root.getAttribute('data-inventory-managed') === 'true';
    const policy = root.getAttribute('data-inventory-policy');
    if (!managed || policy === 'continue') return null;

    return parseIntOrDefault(root.getAttribute('data-inventory-quantity'), null);
  }

  /**
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}}
   */
  canAddToCart() {
    const { value, cartQuantity } = this.getCurrentValues();
    const quantityToAdd = value;
    const variantMax = this.#getVariantQuantityMax();
    const inventoryMax = this.#getInventoryQuantityMax();

    const limits = [variantMax, inventoryMax].filter((limit) => limit !== null);
    const effectiveMax = limits.length > 0 ? Math.min(...limits) : null;

    if (effectiveMax !== null && cartQuantity + quantityToAdd > effectiveMax) {
      return {
        canAdd: false,
        maxQuantity: effectiveMax,
        cartQuantity,
        quantityToAdd,
      };
    }

    return {
      canAdd: true,
      maxQuantity: effectiveMax,
      cartQuantity,
      quantityToAdd,
    };
  }
}

if (!customElements.get('qubo-camera-quantity-selector-component')) {
  customElements.define('qubo-camera-quantity-selector-component', QuboCameraQuantitySelectorComponent);
}
